import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Btn from '../Btn'
import AvatarEditor from '../AvatarEditor'

import { AVATAR_TYPES, pruefeAvatarDatei } from '../../shared/avatarFile'

import photoIcon from '../../assets/icons/photo.svg'

import styles from './style.module.scss'

/**
 * Titelbild wählen, auf 16:9 zuschneiden und übergeben.
 *
 * Wie `UI/AvatarUploader`, nur für ein liegendes Bild: Zwischen Auswahl und
 * Übergabe liegt dasselbe Fenster zum Zuschneiden, geprüft wird mit derselben
 * Funktion (`shared/avatarFile`) — Formate und Größe sollen nicht davon
 * abhängen, an welcher Stelle jemand ein Bild hochlädt.
 *
 * Der feste Ausschnitt ist der Punkt: Die Karten auf dem Dashboard reservieren
 * 16:9. Ein hochkant aufgenommenes Foto würde dort beschnitten, ohne dass die
 * Redaktion es je zu sehen bekommt. Hier entscheidet sie selbst, welcher
 * Ausschnitt es wird.
 *
 * Der Baustein lädt nichts hoch. Er reicht die fertige Datei nach oben; wann
 * sie zum Server geht, entscheidet das Formular — beim Anlegen gibt es die
 * Nachricht noch gar nicht, zu der das Bild gehört.
 */
export default function CoverUploader({ src, className, disabled = false, error, onSelect, onClean }) {
	const { t } = useTranslation()
	const [gewaehlt, setGewaehlt] = useState(null)
	const [meldung, setMeldung] = useState(null)

	// Eine Meldung zur alten Datei gilt nicht mehr, sobald ein Bild dasteht.
	useEffect(() => {
		if (src) setMeldung(null)
	}, [src])

	const waehlen = event => {
		const file = event.target.files?.[0]
		// Zurücksetzen, damit dieselbe Datei erneut gewählt werden kann — sonst
		// bleibt das Feld nach einem Abbruch stumm.
		event.target.value = ''
		if (!file) return

		// Die Prüfung gibt einen Schlüssel zurück, keinen fertigen Satz.
		const fehler = pruefeAvatarDatei(file)
		setMeldung(fehler ? t(fehler) : null)
		if (fehler) return

		setGewaehlt(file)
	}

	const uebernehmen = zugeschnitten => {
		setGewaehlt(null)
		onSelect(zugeschnitten)
	}

	const text = error?.message || meldung

	return (
		<div className={clsx(styles.root, className)}>
			{src ? (
				<div className={styles.preview}>
					<img className={styles.image} src={src} alt="" />
				</div>
			) : (
				<div className={clsx(styles.preview, styles.empty)}>
					<span className={styles.emptyText}>{t('No cover image')}</span>
				</div>
			)}

			<div className={styles.side}>
				{text && <span className={styles.error}>{text}</span>}

				<div className={styles.buttons}>
					<label className={clsx(styles.label, disabled && styles.labelDisabled)}>
						<input
							disabled={disabled}
							className={styles.input}
							type="file"
							accept={AVATAR_TYPES.join(',')}
							onChange={waehlen}
						/>
						<img className={styles.labelIcon} src={photoIcon} alt="" />
						<span>{src ? t('Change cover image') : t('Upload cover image')}</span>
					</label>

					{/*
					 * Mit Beschriftung, nicht als blosses Zeichen: Der Knopf steht
					 * neben „Titelbild ändern", und zwei Symbole nebeneinander
					 * sagen nicht, welches wegnimmt und welches ersetzt.
					 */}
					{!!src && !!onClean && (
						<Btn
							disabled={disabled}
							className={styles.remove}
							onClick={onClean}
							icon="delete"
							iconColor="#22404d"
							// Nicht theme="white": Das ist weisse Schrift auf
							// weissem Grund — sichtbar bleibt nur das Zeichen.
							theme="secondary"
						>
							{t('Remove')}
						</Btn>
					)}
				</div>

				<span className={styles.hint}>{t('coverImageHint')}</span>
			</div>

			{gewaehlt && (
				<AvatarEditor
					file={gewaehlt}
					round={false}
					aspect={16 / 9}
					/*
					 * 1280 Pixel breit: Am größten steht das Bild über dem Text
					 * im Fenster, gut 600 Pixel breit — das Doppelte lässt Luft
					 * für Bildschirme mit doppelter Punktdichte und hält die
					 * Datei trotzdem klein.
					 */
					breite={1280}
					dateiname="titelbild.jpg"
					title={t('Crop cover image')}
					hint={t('coverEditorHint')}
					onCancel={() => setGewaehlt(null)}
					onDone={uebernehmen}
				/>
			)}
		</div>
	)
}

CoverUploader.propTypes = {
	/** Anzeigbare Adresse des aktuellen Bildes — gespeichert oder eben gewählt. */
	src: PropTypes.string,
	className: PropTypes.string,
	disabled: PropTypes.bool,
	/** Meldung von außen, etwa nach einem gescheiterten Hochladen. */
	error: PropTypes.object,
	/** Bekommt die zugeschnittene Datei. */
	onSelect: PropTypes.func.isRequired,
	/** Ohne diese Angabe gibt es keinen Knopf zum Entfernen. */
	onClean: PropTypes.func
}
